import { useEffect, useRef, useState } from 'react'
import type { User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { withinDeadline } from '@/lib/platformDashboard'
import { PlatformAuthContext } from './platformSession'
import { PlatformSessionGate } from '@/lib/platformSessionGate'

// This boundary reads Auth only. It never initializes customer role, office,
// preview, sales or accounting state for an internal Hella viewer.
export function PlatformAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [sessionError, setSessionError] = useState('')
  const [sessionVersion, setSessionVersion] = useState(0)
  const gate = useRef<PlatformSessionGate | null>(null)
  const mounted = useRef(false)
  if (gate.current === null) { gate.current = new PlatformSessionGate(sessionStorage) }
  useEffect(() => {
    mounted.current = true
    let active = true
    let changed = false
    const intent = gate.current!
    const bootstrapRevision = intent.revision
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      changed = true
      if (active && !intent.blocked) { setUser(session?.user ?? null); setSessionVersion(value => value + 1); setLoading(false) }
    })
    if (intent.blocked) queueMicrotask(() => { if (active) setLoading(false) })
    else withinDeadline(supabase.auth.getSession()).then(result => {
      if (active && !changed && !intent.blocked && intent.revision === bootstrapRevision) { setUser(result.data.session?.user ?? null); setLoading(false) }
    }, () => { if (active && !changed && !intent.blocked) { setUser(null); setLoading(false) } })
    return () => { active = false; mounted.current = false; intent.invalidate(); data.subscription.unsubscribe() }
  }, [])
  async function signIn(email: string, password: string) {
    const intent = gate.current!
    const revision = intent.close()
    setSessionError('')
    const result = await withinDeadline(supabase.auth.signInWithPassword({ email, password }))
    if (result.error) throw result.error
    if (!result.data.user || !result.data.session || !mounted.current || !intent.passwordSucceeded(revision)) throw new Error('Sign-in verification unavailable')
    // SIGNED_IN is deliberately ignored while the password request is pending.
    setUser(result.data.user)
    setLoading(false)
  }
  async function signOut() {
    const intent = gate.current!
    const revision = intent.close()
    setUser(null); setLoading(false); setSessionError('')
    try {
      const result = await withinDeadline(supabase.auth.signOut({ scope: 'local' }))
      if (result.error) throw result.error
    } catch {
      if (intent.revision === revision) setSessionError('Sign-out verification unavailable. Private costs remain closed in this tab.')
    }
  }
  return <PlatformAuthContext.Provider value={{ user, sessionVersion, loading, signIn, signOut, sessionError }}>{children}</PlatformAuthContext.Provider>
}
